import * as React from 'react';

import {
  enumPurchaseLineItemType,
  PurchaseLineItemType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { cn, MaterialSymbol, Row, ThemeColor } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCarFront,
  LucideCog,
  LucideTag,
  LucideTruck,
} from 'lucide-react';

import { InventoryItemsModal } from '../../../_shared/InventoryItemsModal';
import {
  $itemsInterfaceHighlightedMaterialIds,
  $itemsInterfaceHighlightedSectionTitle,
  $itemsInterfaceSuggestedPrices,
  resolveSuggestedPrice,
} from '../_state';
import { ItemFieldProps } from '../_types';
import { useDialogModalStyle } from '../hooks/useDialogModalStyle';

import { useInventoryGroupStyle } from '~/modules/trade/hooks/useInventoryGroupStyle';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectMaterialTouch } from '~/shared/components/select-fields/SelectMaterialTouch';
import { useUIPreferences } from '~/shared/hooks';

const iconBySpecialType: Record<
  PurchaseLineItemType,
  React.ReactElement | null
> = {
  [enumPurchaseLineItemType.CatalyticConverter]: <LucideCog size={16} />,
  [enumPurchaseLineItemType.Freight]: <LucideTruck size={16} />,
  [enumPurchaseLineItemType.Mixed]: <LucideTag size={16} />,
  [enumPurchaseLineItemType.Raw]: null,
  [enumPurchaseLineItemType.Tagged]: <LucideTag size={16} />,
  [enumPurchaseLineItemType.Vehicle]: <LucideCarFront size={16} />,
};

const materialColorByLineItemType: Record<string, ThemeColor> = {
  [enumPurchaseLineItemType.CatalyticConverter]: 'slate',
  [enumPurchaseLineItemType.Freight]: 'teal',
  [enumPurchaseLineItemType.Mixed]: 'slate',
  [enumPurchaseLineItemType.Vehicle]: 'primary',
};

//
// ⚛️ ⚛️ ⚛️ INVENTORY ITEM FIELD ⚛️ ⚛️ ⚛️
//
export const InventoryItemField: React.FC<
  ItemFieldProps & { onAfterSelect?: () => void }
> = ({
  lineItem,
  symbolDescription,
  disabled,
  onUpdateItem,
  onKeyDown,
  onAfterSelect,
}) => {
  // Translations
  const t = useTranslations();
  const labels = t.scaleTicketPro.inventoryItemField;

  // States
  const [selectedMnemonic, setSelectedMnemonic] = React.useState<
    string | undefined
  >();
  const [resolvedColorHue, setResolvedColorHue] = React.useState<number | null>(
    null,
  );
  const [materialModalOpen, setMaterialModalOpen] = React.useState(false);

  // Atoms
  const highlightedMaterialIds = useAtomValue(
    $itemsInterfaceHighlightedMaterialIds,
  );
  const highlightedSectionTitle = useAtomValue(
    $itemsInterfaceHighlightedSectionTitle,
  );
  const suggestedPrices = useAtomValue($itemsInterfaceSuggestedPrices);

  // Data
  const { touchModeEnabled } = useUIPreferences();
  const groupStyle = useInventoryGroupStyle({ colorHue: resolvedColorHue });
  const modalStyle = useDialogModalStyle();

  // Memoized values
  const hasHighlightedMaterials = highlightedMaterialIds.length > 0;

  const materialSymbolColor = React.useMemo(
    () => materialColorByLineItemType[lineItem.type],
    [lineItem.type],
  );

  const fixedValue = React.useMemo<Record<string, string>>(
    () => ({
      [enumPurchaseLineItemType.Freight]: labels.fixedFreightCharge,
      [enumPurchaseLineItemType.Mixed]: labels.fixedMixedMaterial,
    }),
    [labels.fixedFreightCharge, labels.fixedMixedMaterial],
  );

  const prefixType = React.useMemo<Record<string, string>>(
    () => ({
      [enumPurchaseLineItemType.CatalyticConverter]: labels.prefixConverter,
      [enumPurchaseLineItemType.Vehicle]: labels.prefixVehicleBatch,
    }),
    [labels.prefixConverter, labels.prefixVehicleBatch],
  );

  // Callbacks
  const handleEntityResolved = React.useCallback(
    (entity: {
      itemGroup?: { id?: string; colorHue?: number | null } | null;
      skuNumber?: string;
      effectivePricingUnit?: string;
      colorHue?: number | null;
    }) => {
      setSelectedMnemonic(entity?.skuNumber ?? undefined);
      setResolvedColorHue(
        entity?.colorHue ?? entity?.itemGroup?.colorHue ?? null,
      );
      if (entity?.effectivePricingUnit) {
        onUpdateItem?.(lineItem.id, {
          pricingUnit: entity.effectivePricingUnit as Unit,
        });
      }
    },
    [lineItem.id, onUpdateItem],
  );

  const handleTouchTriggerClick = React.useCallback(() => {
    setMaterialModalOpen(true);
  }, []);

  const buildItemUpdates = React.useCallback(
    (itemId: string | null): Partial<typeof lineItem> => {
      const updates: Partial<typeof lineItem> = { itemId, unitPrice: null };
      const suggested = resolveSuggestedPrice(suggestedPrices, itemId);
      if (suggested) {
        updates.unitPrice = suggested.unitPrice;
        updates.pricingUnit = suggested.pricingUnit;
      }
      return updates;
    },
    [suggestedPrices],
  );

  const handleMaterialSelect = React.useCallback(
    (materialId: string) => {
      onUpdateItem?.(lineItem.id, buildItemUpdates(materialId));
      setMaterialModalOpen(false);
      onAfterSelect?.();
    },
    [lineItem.id, onUpdateItem, onAfterSelect],
  );

  const handleModalOpenChange = React.useCallback((isOpen: boolean) => {
    if (!isOpen) setMaterialModalOpen(false);
  }, []);

  const hasFixedLabel = !!fixedValue[lineItem.type];

  return (
    <Row
      xCenter
      gap2
      className="@InventoryItemField h-full"
      onKeyDown={onKeyDown}
    >
      <MaterialSymbol
        description={symbolDescription || selectedMnemonic}
        style={!materialSymbolColor ? groupStyle : undefined}
        color={materialSymbolColor}
        className="w-[35px] max-w-[35px]"
        symbolIcon={iconBySpecialType[lineItem.type]}
      />

      {hasFixedLabel && (
        <Row
          xCenter
          className={cn(
            'h-full w-full rounded-md bg-neutral/5 px-3',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <span className="w-full text-left text-sm">
            {fixedValue[lineItem.type]}
          </span>
        </Row>
      )}
      {!hasFixedLabel && (
        <>
          <SelectMaterialTouch
            className="h-full min-h-full flex-1"
            prefix={prefixType[lineItem.type]}
            placeholder={labels.placeholder}
            value={lineItem.itemId}
            onEntityResolved={handleEntityResolved}
            onChange={(itemId) => {
              onUpdateItem?.(lineItem.id, buildItemUpdates(itemId ?? null));
              onAfterSelect?.();
            }}
            isDisabled={disabled}
            onTriggerClick={
              touchModeEnabled ? handleTouchTriggerClick : undefined
            }
            sortOptions={
              hasHighlightedMaterials
                ? (options) => {
                    const soIds = new Set(highlightedMaterialIds);
                    const soGroup = highlightedSectionTitle ?? '';
                    return options.map((opt) => ({
                      ...opt,
                      group: soIds.has(opt.id)
                        ? `  ${soGroup}`
                        : labels.allMaterials,
                    }));
                  }
                : undefined
            }
          />
          {touchModeEnabled && (
            <InventoryItemsModal
              isOpen={materialModalOpen}
              mode="items"
              onOpenChange={handleModalOpenChange}
              onSelectMaterial={handleMaterialSelect}
              modalStyle={modalStyle}
              initialSelectedItemId={lineItem.itemId}
              highlightedMaterialIds={highlightedMaterialIds}
              highlightedSectionTitle={highlightedSectionTitle}
            />
          )}
        </>
      )}
    </Row>
  );
};
